import { Component, effect, OnDestroy, OnInit, untracked } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { UserService } from 'src/app/services/user.service';
import { MdrService } from 'src/app/services/mdr.service';
import { SpinnerService } from 'src/app/_components/spinner/spinner.service';
import { ModalErrorComponent } from 'src/app/modal/modal-gestioneErrori/modal-error/modal-error.component';
import { ModalSuccessComponent } from 'src/app/modal/modal-gestioneErrori/modal-success/modal-success.component';
import { GenericConfirmationComponent } from 'src/app/modal/generic-confirmation/generic-confirmation.component';
import { MyErrorHandler, RESTAURANT_IN_REQUESTS_MESSAGE } from 'src/app/constants/error';
import { Ship } from 'src/app/model';
import { RestaurantDetail, RestaurantListItem } from 'src/app/model/mdr.model';
import {
  RestaurantConfigDialogComponent,
  RestaurantConfigDialogData,
} from 'src/app/modal/modal-mdr/restaurant-config-dialog/restaurant-config-dialog.component';
import { MdrRestaurantsListStore, MdrSortState } from '../stores';

/** Backend error code returned when the restaurant is still referenced by an MDR request. */
const RESTAURANT_IN_REQUESTS_ERROR = 'RESTAURANT_FOUND_IN_REQUESTS';

@Component({
  selector: 'app-mdr-restaurants-tab',
  templateUrl: './restaurants-tab.component.html',
  styleUrls: ['./restaurants-tab.component.scss'],
  standalone: false,
  providers: [MdrRestaurantsListStore],
})
export class RestaurantsTabComponent implements OnInit, OnDestroy {

  private ngUnsubscribe = new Subject<void>();

  availableShips: Ship[] = [];
  selectedShips: Ship[] = [];

  displayedColumns: string[] = ['code', 'name', 'shifts', 'ship', 'edit', 'delete'];

  sortBy: string = 'code';
  sortOrder: 'asc' | 'desc' = 'asc';

  private storeSpinnerShown = false;

  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private mdrService: MdrService,
    private spinner: SpinnerService,
    public store: MdrRestaurantsListStore,
  ) {
    effect(() => {
      const isLoading = this.store.loading();
      if (isLoading && !this.storeSpinnerShown) {
        this.spinner.show();
        this.storeSpinnerShown = true;
      } else if (!isLoading && this.storeSpinnerShown) {
        this.spinner.hide();
        this.storeSpinnerShown = false;
      }
    });

    effect(() => {
      const err = this.store.loadError();
      if (!err) return;
      untracked(() => {
        this.dialog.open(ModalErrorComponent, { data: MyErrorHandler.getText(err, true) }, );
        this.store.loadError.set(null);
      });
    });
  }

  ngOnInit(): void {
    this.availableShips = this.userService.ships;

    if (this.availableShips.length === 1) {
      this.selectedShips = [...this.availableShips];
    }

    this.store.init(this.buildFilters(), this.buildSortState(), 25);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  search(): void {
    this.store.setFilters(this.buildFilters());
  }

  onPageChange(event: PageEvent): void {
    if (event.pageSize !== this.store.pageSize()) {
      this.store.setPageSize(event.pageSize);
      return;
    }
    this.store.goToPage(event.pageIndex + 1);
  }

  onSort(col: string): void {
    if (col === this.sortBy) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col;
      this.sortOrder = 'asc';
    }
    this.store.setSort(this.buildSortState());
  }

  openCreate(): void {
    const ref = this.dialog.open(RestaurantConfigDialogComponent, {
      width: '720px',
      autoFocus: 'dialog',
      data: { mode: 'create', ships: this.availableShips } as RestaurantConfigDialogData,
    });
    ref.afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: RestaurantDetail | undefined) => {
        if (result) this.onMutationSuccess();
      });
  }

  openEdit(row: RestaurantListItem): void {
    this.spinner.show();
    this.mdrService.getRestaurant(row.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (detail: RestaurantDetail) => {
          this.spinner.hide();
          const ref = this.dialog.open(RestaurantConfigDialogComponent, {
            width: '720px',
            autoFocus: 'dialog',
            data: { mode: 'edit', restaurant: detail, ships: this.availableShips } as RestaurantConfigDialogData,
          });
          ref.afterClosed()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((result: RestaurantDetail | undefined) => {
              if (result) this.onMutationSuccess();
            });
        },
        error: (err) => this.showError(err),
      });
  }

  deleteRestaurant(row: RestaurantListItem): void {
    const ref = this.dialog.open(GenericConfirmationComponent, {
      data: { message: `Delete restaurant "${row.name}"? This cannot be undone.`, english: true },
    });
    ref.afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.spinner.show();
        this.mdrService.deleteRestaurant(row.id)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe({
            next: () => {
              this.spinner.hide();
              this.onMutationSuccess();
            },
            error: (err) => this.handleDeleteError(err),
          });
      });
  }

  private onMutationSuccess(): void {
    this.store.refresh();
    this.dialog.open(ModalSuccessComponent, { data: { english: true } });
  }

  private showError(err: unknown): void {
    this.spinner.hide();
    this.dialog.open(ModalErrorComponent, { data: MyErrorHandler.getText(err, true) });
  }

  private handleDeleteError(err: any): void {
    this.spinner.hide();
    const codes: string[] = (err?.error?.errors ?? []).map((e: { code?: string }) => e?.code);
    if (codes.includes(RESTAURANT_IN_REQUESTS_ERROR)) {
      this.dialog.open(ModalErrorComponent, { data: { customText: RESTAURANT_IN_REQUESTS_MESSAGE, english: true } });
      return;
    }
    this.dialog.open(ModalErrorComponent, { data: MyErrorHandler.getText(err, true) });
  }

  private buildFilters() {
    return {
      ships: this.selectedShips.length ? this.selectedShips.map(s => s.id_ship) : undefined,
    };
  }

  private buildSortState(): MdrSortState {
    return { by: this.sortBy, order: this.sortOrder };
  }
}
